import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { authStore, api } from "@/lib/apiClient";

/**
 * Guards admin routes. Persistent-login rules:
 *
 * - If an access token is present in localStorage, we treat the user as
 *   authed. The axios interceptor will silently refresh on any 401.
 * - If only the refresh token is present (access expired between visits),
 *   we attempt a one-shot refresh before deciding to redirect.
 */
export default function RequireAuth({ children }) {
  const hasAccess = Boolean(authStore.getAccess());
  const hasRefresh = Boolean(authStore.getRefresh());
  const [state, setState] = useState(hasAccess ? "authed" : hasRefresh ? "hydrating" : "unauthed");

  useEffect(() => {
    if (state !== "hydrating") return;
    let cancelled = false;
    api
      .post("/auth/refresh", { refresh_token: authStore.getRefresh() })
      .then((r) => {
        if (cancelled) return;
        authStore.set(r.data);
        setState("authed");
      })
      .catch(() => {
        if (cancelled) return;
        authStore.clear();
        setState("unauthed");
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state === "unauthed") return <Navigate to="/admin/login" replace />;
  if (state === "hydrating") {
    return (
      <div
        className="min-h-screen grid place-items-center text-sm text-muted-foreground"
        data-testid="require-auth-hydrating"
      >
        Restoring your session…
      </div>
    );
  }
  return children;
}
